import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { attendanceAPI } from '../services/api';
import { AbsenceType } from '../types';
import { showCustomAlert } from './CustomAlert';

const AbsenceTypes: React.FC = () => {
  const [items, setItems] = useState<AbsenceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<AbsenceType | null>(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState<{ name: string; type: 'paid' | 'unpaid'; defaultDays: number }>({
    name: '',
    type: 'paid',
    defaultDays: 1
  });

  const typeOptions = useMemo(
    () => [
      { label: 'Plaćeno', value: 'paid' as const },
      { label: 'Neplaćeno', value: 'unpaid' as const }
    ],
    []
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await attendanceAPI.getAbsenceTypes(showAll ? undefined : { isActive: true });
      setItems(data);
    } catch (e: any) {
      console.error('Error fetching absence types:', e);
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju odsustava');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const openCreate = () => {
    setEditing(null);
    setError('');
    setForm({ name: '', type: 'paid', defaultDays: 1 });
    setModalVisible(true);
  };

  const openEdit = (x: AbsenceType) => {
    setEditing(x);
    setError('');
    setForm({ name: x.name || '', type: x.type, defaultDays: x.defaultDays || 1 });
    setModalVisible(true);
  };

  const submit = async () => {
    try {
      setError('');
      if (!form.name.trim()) {
        setError('Naziv odsustva je obavezan.');
        return;
      }
      if (!form.defaultDays || form.defaultDays < 1) {
        setError('Broj dana odsustva mora biti najmanje 1.');
        return;
      }

      const payload = {
        name: form.name.trim(),
        type: form.type,
        defaultDays: form.defaultDays
      };

      if (editing) {
        await attendanceAPI.updateAbsenceType(editing._id, payload);
        showCustomAlert.success('Odsustvo je ažurirano');
      } else {
        await attendanceAPI.createAbsenceType(payload);
        showCustomAlert.success('Odsustvo je dodato');
      }
      setModalVisible(false);
      fetchData();
    } catch (e: any) {
      console.error('Error saving absence type:', e);
      setError(e?.response?.data?.message || 'Greška pri čuvanju odsustva');
    }
  };

  const deactivate = (x: AbsenceType) => {
    confirmDialog({
      header: 'Potvrda',
      icon: 'pi pi-exclamation-triangle',
      message: `Da li ste sigurni da želite da obrišete odsustvo "${x.name}"?`,
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await attendanceAPI.deleteAbsenceType(x._id);
          showCustomAlert.success('Odsustvo je obrisano');
          fetchData();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri brisanju odsustva');
        }
      }
    });
  };

  const statusTemplate = (x: AbsenceType) => {
    const label = x.isActive ? 'Aktivno' : 'Neaktivno';
    const cls = x.isActive ? 'bg-green-500' : 'bg-gray-500';
    return (
      <span className={`inline-flex align-items-center gap-2 px-3 py-1 text-white text-sm border-round ${cls}`}>
        <i className={`pi ${x.isActive ? 'pi-check' : 'pi-times'}`} />
        {label}
      </span>
    );
  };

  const actionsTemplate = (x: AbsenceType) => {
    return (
      <div className="flex gap-2 justify-content-end">
        <Button
          icon="pi pi-pencil"
          rounded
          severity="warning"
          className="p-button-sm"
          tooltip="Izmeni"
          tooltipOptions={{ position: 'left' }}
          onClick={() => openEdit(x)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          severity="danger"
          className="p-button-sm"
          tooltip="Obriši"
          tooltipOptions={{ position: 'left' }}
          onClick={() => deactivate(x)}
        />
      </div>
    );
  };

  return (
    <div className="p-4">
      <Card>
        <h2 className="m-0">Individualna odsustva</h2>

        <div className="flex justify-content-between align-items-center mb-4 mt-4 p-3 border-1 border-round surface-border bg-white">
          <div className="flex gap-2">
            <Button label="Dodaj odsustvo" icon="pi pi-plus" onClick={openCreate} severity="success" />
            <Button
              label={showAll ? 'Prikaži aktivna' : 'Prikaži sve'}
              icon="pi pi-eye"
              className="p-button-outlined"
              onClick={() => setShowAll((v) => !v)}
            />
          </div>
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={fetchData} />
        </div>

        <DataTable
          value={items}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          currentPageReportTemplate="Prikaz {first} do {last} od ukupno {totalRecords} odsustava"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          emptyMessage="Nema odsustava"
          sortMode="multiple"
          className="p-datatable-sm"
        >
          <Column field="name" header="Naziv" sortable />
          <Column header="Status" body={statusTemplate} style={{ width: '160px' }} />
          <Column header="" body={actionsTemplate} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni odsustvo' : 'Novo odsustvo'}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '560px' }}
        draggable={false}
        className="p-fluid"
      >
        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="field">
          <label className="block mb-2">
            Naziv odsustva <span className="text-red-500">*</span>
          </label>
          <InputText
            value={form.name}
            placeholder="npr. Smrtni slučaj u porodici"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="block mb-2">
            Tip odsustva <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={form.type}
            options={typeOptions}
            onChange={(e) => setForm({ ...form, type: e.value })}
            appendTo="self"
          />
        </div>

        <div className="field">
          <label className="block mb-2">
            Broj dana odsustva <span className="text-red-500">*</span>
          </label>
          <InputNumber value={form.defaultDays} min={1} onValueChange={(e) => setForm({ ...form, defaultDays: e.value ?? 1 })} />
        </div>

        <div className="flex justify-content-end gap-2 mt-2">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setModalVisible(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={submit} />
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default AbsenceTypes;


